import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import portalApi, { apiError } from './portalApi';
import { T } from './portalTheme';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { PortalAppShell, PageContainer, PageHeader, Card, CardHeader, Field, TextInput, PasswordInput, Button, Alert, Avatar, IC } from './PortalWidgets';

// /questionnaire/portal/account -- profile (name), password, and sign out.
export default function PortalAccount() {
  const { account, updateAccount, logout } = usePortalAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState(account?.name || '');
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const saveName = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingName(true);
    setNameMsg(null);
    try {
      const { data } = await portalApi.patch('/v1/questionnaire-responses/portal/account', { name });
      updateAccount(data.account);
      queryClient.invalidateQueries({ queryKey: ['portal', 'me'] });
      setNameMsg({ tone: 'success', text: 'Your name was updated.' });
    } catch (err) {
      setNameMsg({ tone: 'error', text: apiError(err, 'Could not save your name.') });
    } finally {
      setSavingName(false);
    }
  };

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    if (next.length < 8) { setPwMsg({ tone: 'error', text: 'Your new password must be at least 8 characters.' }); return; }
    if (next !== confirm) { setPwMsg({ tone: 'error', text: "The new passwords don't match." }); return; }
    setSavingPw(true);
    try {
      await portalApi.post('/v1/questionnaire-responses/portal/account/password', { current_password: current, new_password: next });
      setCurrent(''); setNext(''); setConfirm('');
      setPwMsg({ tone: 'success', text: 'Your password was changed.' });
    } catch (err) {
      setPwMsg({ tone: 'error', text: apiError(err, 'Could not change your password.') });
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <PortalAppShell>
      <PageContainer max={760}>
        <PageHeader title="Account settings" subtitle="Manage how you sign in to the FluxWatch vendor portal." />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Card>
            <CardHeader title="Profile" subtitle="Your name is shown to the companies you answer for and to your collaborators." />
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
              <Avatar name={name || account?.email || '?'} size={52} />
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{name || 'No name set'}</div>
                <div style={{ fontSize: 13, color: T.muted }}>{account?.email}</div>
              </div>
            </div>
            <form onSubmit={saveName} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="pw-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Full name">
                  <TextInput value={name} onChange={e => setName(e.target.value)} placeholder="Jordan Lee" autoComplete="name" maxLength={120} />
                </Field>
                <Field label="Email" hint="Your email identifies your account and can't be changed.">
                  <TextInput value={account?.email || ''} readOnly />
                </Field>
              </div>
              {nameMsg && <Alert tone={nameMsg.tone}>{nameMsg.text}</Alert>}
              <div><Button type="submit" loading={savingName} disabled={name === (account?.name || '')}>Save changes</Button></div>
            </form>
          </Card>

          <Card>
            <CardHeader title="Password" subtitle="Use at least 8 characters. You'll stay signed in on this device." />
            <form onSubmit={changePassword} style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 420 }}>
              <Field label="Current password">
                <PasswordInput value={current} onChange={e => setCurrent(e.target.value)} autoComplete="current-password" required />
              </Field>
              <Field label="New password">
                <PasswordInput value={next} onChange={e => setNext(e.target.value)} autoComplete="new-password" required showStrength />
              </Field>
              <Field label="Confirm new password" error={confirm && next !== confirm ? "Passwords don't match." : undefined}>
                <PasswordInput value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" required />
              </Field>
              {pwMsg && <Alert tone={pwMsg.tone}>{pwMsg.text}</Alert>}
              <div><Button type="submit" loading={savingPw} disabled={!current || !next || !confirm}>Change password</Button></div>
            </form>
          </Card>

          <Card>
            <CardHeader title="Sign out" subtitle="Sign out of the vendor portal on this device." />
            <Button variant="secondary" icon={IC.logout} onClick={() => { logout(); navigate('/questionnaire/portal/login', { replace: true }); }}>Sign out</Button>
          </Card>
        </div>
      </PageContainer>
    </PortalAppShell>
  );
}
